import { createContext, memo, useContext, useEffect, useRef, type ReactNode } from 'react';
import type { Mode } from '@/shared/mode/types';

/**
 * PaneContext — the source of truth for "is THIS mode pane currently the
 * active one?" for components nested inside a Pane.
 *
 * Why we need this: PersistentPanes passes a frozen `location` prop to each
 * inactive pane's <Routes>, so components inside an inactive pane that call
 * useLocation() get the frozen URL — which means hooks like useTabHistory
 * (which derive the current mode from useLocation) report the pane's own
 * mode as still active. That's correct for routing/matching, but wrong for
 * "did the user just switch away from me?" effects (e.g., pause my video).
 *
 * Components that need to react to the REAL active mode read from this
 * context instead of useLocation.
 */
type PaneContextValue = { active: boolean; mode: Mode };
const PaneContext = createContext<PaneContextValue | null>(null);

export function usePane(): PaneContextValue {
  const ctx = useContext(PaneContext);
  if (!ctx) {
    throw new Error('usePane() must be used inside a <Pane> from PersistentPanes');
  }
  return ctx;
}

type PaneProps = {
  active: boolean;
  mode: Mode;
  children: ReactNode;
  testId?: string;
};

export const Pane = memo(function Pane({ active, mode, children, testId }: PaneProps) {
  // Per-pane window scroll memo: save scrollY when going inactive, restore
  // when going active. Keeps the persistent-panes feel even though the
  // mobile layout uses window scrolling (not an internal scroll container).
  const savedScroll = useRef(0);
  const wasActive = useRef(active);

  useEffect(() => {
    if (wasActive.current && !active) {
      savedScroll.current = window.scrollY;
    } else if (!wasActive.current && active) {
      // Restore on next frame so the unhidden content has laid out.
      const y = savedScroll.current;
      requestAnimationFrame(() => window.scrollTo(0, y));
    }
    wasActive.current = active;
  }, [active]);

  return (
    <PaneContext.Provider value={{ active, mode }}>
      <div hidden={!active} className="h-full" data-testid={testId}>
        {children}
      </div>
    </PaneContext.Provider>
  );
});
